/**
 * The hand.
 *
 * Cards were authored, framed and shipped without ever reaching the screen. This
 * is where they become a thing you hold and spend.
 *
 * Laid out as an overlapping fan anchored to the bottom edge — the physical
 * gesture the game is imitating — with the playable ones raised. Unplayable cards
 * stay visible and dimmed rather than being hidden: knowing you are holding a 6
 * you cannot spend *yet* is the tension the card system exists for.
 */

import { useState } from 'react';
import type { CardDefRef, GameSession } from '@lastroll/engine';
import { isPlayable } from '@lastroll/engine';
import { cardUri } from './art.js';

const TYPE_LABEL: Record<string, string> = {
  EVENT: '사건',
  ACTION: '행동',
  FATE: '운명',
  KNOWLEDGE: '지식',
  MEMORY: '기억',
  RELATIONSHIP: '관계',
  CURSE: '저주',
  LEGEND: '전설',
};

export interface HandProps {
  session: GameSession;
  onPlay: (cardId: string) => void;
}

export function Hand({ session, onPlay }: HandProps): JSX.Element | null {
  const [open, setOpen] = useState<string | null>(null);
  const hand = session.hand;
  if (hand.length === 0) return null;

  const ctx = session.cardContext;
  const playableCount = hand.filter((c) => isPlayable(c, ctx)).length;
  const detail = open ? hand.find((c) => c.id === open) : null;

  return (
    <>
      <div className="hand">
        <div className="hand__label">
          손패 {hand.length}장
          {playableCount > 0 ? (
            <span className="hand__ready"> · {playableCount}장 사용 가능</span>
          ) : (
            <span className="muted"> · 지금은 낼 수 없다</span>
          )}
        </div>
        <div className="hand__fan">
          {hand.map((card, i) => {
            const can = isPlayable(card, ctx);
            return (
              <button
                key={`${card.id}-${i}`}
                type="button"
                className={`card${can ? ' card--playable' : ''}`}
                style={{ zIndex: i }}
                onClick={() => setOpen(card.id)}
                aria-label={`${card.name} — ${TYPE_LABEL[card.type] ?? card.type}${can ? ', 사용 가능' : ''}`}
              >
                <img src={cardUri(card)} alt="" />
              </button>
            );
          })}
        </div>
      </div>

      {detail && (
        <CardDetail
          card={detail}
          playable={isPlayable(detail, ctx)}
          context={ctx}
          onPlay={() => {
            onPlay(detail.id);
            setOpen(null);
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

const CONTEXT_HINT: Record<string, string> = {
  roll: '주사위를 굴린 뒤',
  move: '이동할 때',
  encounter: '조우 중',
  combat: '전투 중',
  anytime: '언제든',
};

function CardDetail({
  card,
  playable,
  context,
  onPlay,
  onClose,
}: {
  card: CardDefRef;
  playable: boolean;
  context: string;
  onPlay: () => void;
  onClose: () => void;
}): JSX.Element {
  const when = card.play?.in.map((c) => CONTEXT_HINT[c] ?? c).join(' / ');
  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={card.name}>
      <button type="button" className="sheet__scrim" onClick={onClose} aria-label="닫기" />
      <div className="sheet__body">
        <img className="sheet__card" src={cardUri(card)} alt="" />
        <div className="sheet__info">
          <h3 className="sheet__name">{card.name}</h3>
          <p className="muted">
            {TYPE_LABEL[card.type] ?? card.type} · {card.rarity}
          </p>
          <p className="panel__body">{card.text}</p>
          {card.play ? (
            <p className="muted">낼 수 있는 때: {when}</p>
          ) : (
            <p className="muted">낼 수 없는 카드다. 네가 한 일의 기록으로 남는다.</p>
          )}
          {card.play?.remove && <p className="muted">한 번 쓰면 덱에서 사라진다.</p>}
          <div className="sheet__actions">
            <button type="button" className="roll-btn roll-btn--ghost" onClick={onClose}>
              닫기
            </button>
            {card.play && (
              <button type="button" className="roll-btn" disabled={!playable} onClick={onPlay}>
                {playable ? '이 카드를 낸다' : `지금은 낼 수 없다 (${CONTEXT_HINT[context] ?? context})`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
